import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, X, Eye, Loader2, Calendar, Clock, MapPin, Link2, Users, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import AdminPageBackground from "@/components/admin/AdminPageBackground";

// Submission interface
interface Submission {
  id: string;
  user_id: string;
  submission_type: string;
  status: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_ar: string | null;
  external_link: string | null;
  category: string | null;
  x_space_url: string | null;
  scheduled_at: string | null;
  social_type: string | null;
  social_link: string | null;
  members_count: number | null;
  admin_notes: string | null;
  created_at: string;
}

const Submissions = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const [adminNotes, setAdminNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubmissions();
  }, [activeTab]);

  const fetchSubmissions = async () => {
    setLoading(true);
    const statusFilter = activeTab as "pending" | "approved" | "rejected";
    const { data, error } = await supabase
      .from("user_submissions")
      .select("*")
      .eq("status", statusFilter)
      .order("created_at", { ascending: false });

    if (data) {
      setSubmissions(data as Submission[]);
    }
    setLoading(false);
  };

  const handleAction = async () => {
    if (!selectedSubmission) return;
    
    setProcessing(true);

    try {
      const { error } = await supabase
        .from("user_submissions")
        .update({
          status: actionType === "approve" ? "approved" : "rejected",
          admin_notes: adminNotes || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", selectedSubmission.id);

      if (error) throw error;

      if (actionType === "approve") {
        if (selectedSubmission.submission_type === "event") {
          await supabase.from("scraped_events").insert({
            title: selectedSubmission.title,
            title_ar: selectedSubmission.title_ar,
            description: selectedSubmission.description,
            description_ar: selectedSubmission.description_ar,
            date: selectedSubmission.date,
            start_time: selectedSubmission.start_time,
            end_time: selectedSubmission.end_time,
            location: selectedSubmission.location,
            location_ar: selectedSubmission.location_ar,
            external_link: selectedSubmission.external_link,
            category: selectedSubmission.category,
            source_name: "user_submission",
            source_url: "user_submission",
            is_published: true,
          });
        } else if (selectedSubmission.submission_type === "x_space") {
          const { data: maxId } = await supabase
            .from("scheduled_x_spaces")
            .select("event_id")
            .order("event_id", { ascending: false })
            .limit(1)
            .single();

          const nextEventId = (maxId?.event_id || 0) + 1;

          await supabase.from("scheduled_x_spaces").insert({
            event_id: nextEventId,
            title: selectedSubmission.title,
            title_ar: selectedSubmission.title_ar,
            x_space_url: selectedSubmission.x_space_url,
            scheduled_at: selectedSubmission.scheduled_at || new Date().toISOString(),
          });
        } else if (selectedSubmission.submission_type === "social_account") {
          await supabase.from("social_groups").insert({
            name_en: selectedSubmission.title,
            name_ar: selectedSubmission.title_ar || selectedSubmission.title,
            description_en: selectedSubmission.description || "",
            description_ar: selectedSubmission.description_ar || selectedSubmission.description || "",
            type: selectedSubmission.social_type || "whatsapp",
            link: selectedSubmission.social_link || "",
            members: selectedSubmission.members_count || 0,
          });
        }
      }

      toast.success(
        actionType === "approve"
          ? (isRTL ? "تمت الموافقة بنجاح" : "Approved successfully")
          : (isRTL ? "تم الرفض" : "Rejected")
      );

      setActionDialogOpen(false);
      setSelectedSubmission(null);
      setAdminNotes("");
      fetchSubmissions();
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(isRTL ? "حدث خطأ" : "An error occurred");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary">{isRTL ? "قيد المراجعة" : "Pending"}</Badge>;
      case "approved":
        return <Badge className="bg-green-500">{isRTL ? "موافق عليه" : "Approved"}</Badge>;
      case "rejected":
        return <Badge variant="destructive">{isRTL ? "مرفوض" : "Rejected"}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "event":
        return <Badge variant="outline" className="border-white/20 text-white/80">{isRTL ? "فعالية" : "Event"}</Badge>;
      case "x_space":
        return <Badge variant="outline" className="border-white/20 text-white/80">{isRTL ? "مساحة X" : "X Space"}</Badge>;
      case "social_account":
        return <Badge variant="outline" className="border-white/20 text-white/80">{isRTL ? "حساب تواصل" : "Social Account"}</Badge>;
      default:
        return <Badge variant="outline" className="border-white/20 text-white/80">{type}</Badge>;
    }
  };

  const openViewDialog = (submission: Submission) => {
    setSelectedSubmission(submission);
    setViewDialogOpen(true);
  };

  const openActionDialog = (submission: Submission, action: "approve" | "reject") => {
    setSelectedSubmission(submission);
    setActionType(action);
    setActionDialogOpen(true);
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <div className="p-6" dir={isRTL ? "rtl" : "ltr"}>
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate("/admin-dashboard")} 
          className="flex items-center gap-2 mb-4 text-white/70 hover:text-white hover:bg-white/10"
        >
          {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
          {isRTL ? 'العودة للوحة التحكم' : 'Back to Dashboard'}
        </Button>

        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2 text-white">
            {isRTL ? "طلبات الإضافة" : "User Submissions"}
          </h1>
          <p className="text-white/50">
            {isRTL ? "مراجعة وإدارة طلبات المستخدمين" : "Review and manage user submissions"}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 bg-white/10">
            <TabsTrigger value="pending" className="data-[state=active]:bg-white/20 data-[state=active]:text-white text-white/70">
              {isRTL ? "قيد المراجعة" : "Pending"}
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-white/20 data-[state=active]:text-white text-white/70">
              {isRTL ? "موافق عليها" : "Approved"}
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-white/20 data-[state=active]:text-white text-white/70">
              {isRTL ? "مرفوضة" : "Rejected"}
            </TabsTrigger>
          </TabsList>

          <Card className="bg-white/10 backdrop-blur-md border-white/15">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-12 text-white/50">
                  {isRTL ? "لا توجد طلبات" : "No submissions"}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className="text-white/70">{isRTL ? "العنوان" : "Title"}</TableHead>
                      <TableHead className="text-white/70">{isRTL ? "النوع" : "Type"}</TableHead>
                      <TableHead className="text-white/70">{isRTL ? "الحالة" : "Status"}</TableHead>
                      <TableHead className="text-white/70">{isRTL ? "التاريخ" : "Date"}</TableHead>
                      <TableHead className="text-white/70">{isRTL ? "الإجراءات" : "Actions"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((submission) => (
                      <TableRow key={submission.id} className="border-white/10 hover:bg-white/5">
                        <TableCell className="font-medium text-white">
                          {isRTL && submission.title_ar ? submission.title_ar : submission.title}
                        </TableCell>
                        <TableCell>{getTypeBadge(submission.submission_type)}</TableCell>
                        <TableCell>{getStatusBadge(submission.status)}</TableCell>
                        <TableCell className="text-white/60">
                          {format(new Date(submission.created_at), "PPp", {
                            locale: isRTL ? ar : undefined,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-white/70 hover:text-white hover:bg-white/10"
                              onClick={() => openViewDialog(submission)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {submission.status === "pending" && (
                              <>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                  onClick={() => openActionDialog(submission, "approve")}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  onClick={() => openActionDialog(submission, "reject")}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Tabs>

        {/* View Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {isRTL && selectedSubmission?.title_ar 
                  ? selectedSubmission.title_ar 
                  : selectedSubmission?.title}
              </DialogTitle>
              <DialogDescription className="flex gap-2 pt-2">
                {selectedSubmission && getTypeBadge(selectedSubmission.submission_type)}
                {selectedSubmission && getStatusBadge(selectedSubmission.status)}
              </DialogDescription>
            </DialogHeader>
            
            {selectedSubmission && (
              <div className="space-y-4">
                {selectedSubmission.description && (
                  <div>
                    <h4 className="font-medium mb-1">{isRTL ? "الوصف" : "Description"}</h4>
                    <p className="text-muted-foreground">{selectedSubmission.description}</p>
                  </div>
                )}

                {selectedSubmission.submission_type === "event" && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedSubmission.date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedSubmission.date}</span>
                      </div>
                    )}
                    {selectedSubmission.start_time && (
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedSubmission.start_time} - {selectedSubmission.end_time}</span>
                      </div>
                    )}
                    {selectedSubmission.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedSubmission.location}</span>
                      </div>
                    )}
                    {selectedSubmission.external_link && (
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <a href={selectedSubmission.external_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {isRTL ? "رابط خارجي" : "External Link"}
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.submission_type === "x_space" && (
                  <div className="space-y-2">
                    {selectedSubmission.x_space_url && (
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <a href={selectedSubmission.x_space_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedSubmission.x_space_url}
                        </a>
                      </div>
                    )}
                    {selectedSubmission.scheduled_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{format(new Date(selectedSubmission.scheduled_at), "PPp", { locale: isRTL ? ar : undefined })}</span>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.submission_type === "social_account" && (
                  <div className="space-y-2">
                    {selectedSubmission.social_type && (
                      <Badge variant="outline">{selectedSubmission.social_type}</Badge>
                    )}
                    {selectedSubmission.social_link && (
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4 text-muted-foreground" />
                        <a href={selectedSubmission.social_link} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          {selectedSubmission.social_link}
                        </a>
                      </div>
                    )}
                    {selectedSubmission.members_count && (
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{selectedSubmission.members_count} {isRTL ? "عضو" : "members"}</span>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission.admin_notes && (
                  <div>
                    <h4 className="font-medium mb-1">{isRTL ? "ملاحظات المشرف" : "Admin Notes"}</h4>
                    <p className="text-muted-foreground">{selectedSubmission.admin_notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Action Dialog */}
        <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === "approve" 
                  ? (isRTL ? "موافقة على الطلب" : "Approve Submission")
                  : (isRTL ? "رفض الطلب" : "Reject Submission")}
              </DialogTitle>
              <DialogDescription>
                {actionType === "approve"
                  ? (isRTL ? "سيتم نشر هذا المحتوى في الموقع" : "This content will be published on the site")
                  : (isRTL ? "لن يتم نشر هذا المحتوى" : "This content will not be published")}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {isRTL ? "ملاحظات (اختياري)" : "Notes (optional)"}
                </label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder={isRTL ? "أضف ملاحظات..." : "Add notes..."}
                  className="mt-1"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setActionDialogOpen(false)}>
                {isRTL ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                onClick={handleAction}
                disabled={processing}
                variant={actionType === "approve" ? "default" : "destructive"}
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {actionType === "approve" 
                  ? (isRTL ? "موافقة" : "Approve")
                  : (isRTL ? "رفض" : "Reject")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminPageBackground>
  );
};

export default Submissions;
